import { supabase } from '@/integrations/supabase/client';
import { pushClientError } from '@/utils/clientErrorBuffer';

// Event types
export type OcisEventType =
  | 'ticket_created' | 'ticket_assigned' | 'ticket_started' | 'ticket_paused'
  | 'ticket_resumed' | 'ticket_completed' | 'ticket_date_changed'
  | 'ticket_moved_to_parking_lot' | 'ticket_moved_from_parking_lot'
  | 'ticket_bay_changed' | 'ticket_status_changed' | 'ticket_reassigned' | 'timer_stopped'
  | 'sms_inbound' | 'sms_outbound' | 'customer_message'
  | 'technician_note' | 'advisor_note' | 'manager_note'
  | 'parts_ordered' | 'parts_delayed' | 'parts_arrived'
  | 'reminder_sent' | 'reminder_confirmed' | 'reminder_canceled'
  | 'dvi_finding_added' | 'auto_bump'
  | 'ai_summary' | 'ai_risk_alert' | 'ai_recommendation'
  | 'voice_command' | 'voice_transcript'
  | 'job_added' | 'job_removed' | 'job_note_added' | 'job_note_updated' | 'job_time_edited'
  | 'call_inbound' | 'call_outbound'
  // W4b: human-approved rollover decisions (20270206061200; server-emitted)
  | 'rollover_decision_raised' | 'rollover_decision_recorded' | 'rollover_decision_expired' | 'rollover_plan_updated'
  // W4e: reserved-window schedule conflict marked / moved to Needs Placement / cleared (20270206064000; server-emitted)
  | 'schedule_conflict_updated'
  | 'workflow_report_initialized'
  | 'workflow_complaint_added' | 'workflow_complaint_updated'
  | 'workflow_note_added' | 'workflow_note_updated'
  | 'workflow_inspection_started' | 'workflow_inspection_saved' | 'workflow_inspection_completed'
  | 'inspection_timer_started' | 'inspection_timer_paused' | 'inspection_timer_resumed' | 'inspection_timer_stopped'
  | 'inspection_evidence_uploaded' | 'inspection_evidence_placed' | 'inspection_ai_suggestion'
  | 'workflow_diagnostic_created' | 'workflow_diagnostic_updated'
  | 'workflow_diagnostic_started' | 'workflow_diagnostic_completed'
  | 'dlg_advisory_shown' | 'dlg_override_accepted'
  | 'service_info_acknowledgment' | 'service_info_acknowledgment_updated'
  | 'artifact_created' | 'artifact_linked'
  // OCIS AI Intake — Phase 1, Slice 1 (migration 20260524120000).
  // No emitters in this slice; future slice wires the writers.
  | 'intake_started' | 'intake_audio_uploaded' | 'intake_transcript_ready'
  | 'intake_draft_ready' | 'intake_approved' | 'intake_rejected'
  // OCIS AI Intake — Phase 1, Slice 3b (migration 20260525120000).
  // Emitted by intake-attach-jobs after advisor-selected suggested
  // jobs are attached to the ticket. event_source: 'user'.
  | 'intake_jobs_attached'
  // Lifecycle V2 — FOH Timer Handoff Batch 1. Custody axis events
  // now surface on the OCIS timeline so reviewers see FOH handoff
  // activity alongside timer/parking events. Emitted fire-and-forget
  // from `useTicketCustodyMutation.onSuccess` AFTER the custody
  // transition is durable. The payload carries the standardized
  // handoff metadata (trigger / trigger_reason / etc.) so analytics
  // can split pause-kicks from stop-kicks from manual kicks.
  | 'custody_kick_to_foh'
  | 'custody_foh_accept'
  | 'custody_kick_to_boh'
  | 'custody_boh_accept'
  | 'custody_reject_back'
  | 'custody_reassign_within_foh'
  | 'custody_reassign_within_boh';

export type OcisEventSource = 'system' | 'user' | 'customer' | 'ai' | 'voice';
export type OcisChannel = 'sms' | 'email' | 'app' | 'voice' | 'internal' | 'system';
export type OcisVisibility = 'internal' | 'customer' | 'system';

export interface OcisIngestParams {
  // Required for ticket-attached events
  ticket_id?: string;
  // Required for inbox events
  inbox_thread_id?: string;
  // Required always
  shop_id: string;
  event_type: OcisEventType;
  content: string;
  // Optional
  event_source?: OcisEventSource;
  channel?: OcisChannel;
  visibility?: OcisVisibility;
  author_user_id?: string;
  author_role?: 'technician' | 'advisor' | 'manager';
  customer_id?: string;
  payload?: Record<string, unknown>;
  is_pinned?: boolean;
  parent_event_id?: string;
}

export interface OcisIngestResponse {
  success: boolean;
  event_id?: string;
  thread_id?: string;
  should_summarize?: boolean;
  error?: string;
}

/**
 * Ingest an event into the OCIS thread system.
 * This is the single write path for all communication and ticket events.
 * 
 * @example
 * // Log a ticket created event
 * await ocisIngest({
 *   ticket_id: 'TKT-001',
 *   shop_id: 'shop-uuid',
 *   event_type: 'ticket_created',
 *   content: 'Ticket created for oil change',
 *   event_source: 'system',
 * });
 * 
 * @example
 * // Log a technician note
 * await ocisIngest({
 *   ticket_id: 'TKT-001',
 *   shop_id: 'shop-uuid',
 *   event_type: 'technician_note',
 *   content: 'Found worn brake pads during inspection',
 *   event_source: 'user',
 *   channel: 'app',
 *   author_user_id: 'user-uuid',
 *   author_role: 'technician',
 * });
 */
export async function ocisIngest(params: OcisIngestParams): Promise<OcisIngestResponse> {
  try {
    const { data, error } = await supabase.functions.invoke('ocis-ingest', {
      body: params,
    });

    if (error) {
      // FIX-R132-MED-5: most ocisIngest callers are fire-and-forget event
      // loggers (note added, ticket started, parts arrived, etc.). They
      // discard the returned `{success, error}` because the user-visible
      // mutation has already succeeded. A silent OCIS failure means the
      // operational timeline drops the event — invisible to the operator
      // and to support unless they happen to look at the function logs.
      // Push to the client-error buffer so the next bug report carries
      // the trace.
      console.error('[ocisIngest] Edge function error:', error);
      pushClientError({
        source: 'mutation',
        message: `[ocisIngest] invoke returned error: ${error.message ?? error}`,
        extra: {
          event_type: params.event_type,
          ticket_id: params.ticket_id,
          inbox_thread_id: params.inbox_thread_id,
        },
        shopId: params.shop_id,
      });
      return { success: false, error: error.message };
    }

    return data as OcisIngestResponse;
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    console.error('[ocisIngest] Error:', errorMessage);
    pushClientError({
      source: 'mutation',
      message: `[ocisIngest] invoke threw: ${errorMessage}`,
      stack: (err as { stack?: string } | undefined)?.stack,
      extra: {
        event_type: params.event_type,
        ticket_id: params.ticket_id,
        inbox_thread_id: params.inbox_thread_id,
      },
      shopId: params.shop_id,
    });
    return { success: false, error: errorMessage };
  }
}

/**
 * Helper to log ticket lifecycle events to OCIS.
 * Uses direct DB insert (with thread_id lookup) for reliability,
 * falling back to edge function if the direct insert fails.
 */
export async function logTicketEventToOcis(params: {
  ticketId: string;
  shopId: string;
  eventType: OcisEventType;
  content: string;
  authorUserId?: string;
  authorRole?: 'technician' | 'advisor' | 'manager';
  payload?: Record<string, unknown>;
}): Promise<OcisIngestResponse> {
  try {
    // FIX-R59-MED-3: Verify the ticket actually belongs to the supplied shopId
    // before inserting. RLS on ticket_thread_events is INSERT-scoped by
    // can_access_shop(shop_id), but a Floo-Network user with access to BOTH
    // shop A and shop B could call this helper with `{ticketId: <shop A
    // ticket>, shopId: <shop B>}` and the insert would succeed — producing
    // an event row claiming shop B but pointing at shop A's ticket. Same
    // class as the R58-MED-1 cross-shop FK injection that ocis-ingest now
    // guards against, but for the client-side direct-insert path. Reject
    // mismatch and fall back to the edge function (which has its own
    // validation as belt-and-braces).
    // OCIS-R68-FIX (HIGH-1 merge): select shop_id + customer_id in a single
    // round-trip — the shop-mismatch validation below uses shop_id, and the
    // get-or-create-thread path below needs customer_id. Previously this
    // file had two separate lookups on `tickets` (one here for shop_id, one
    // later for customer_id) which doubled DB traffic per OCIS write.
    const { data: ticketRow, error: ticketLookupErr } = await supabase
      .from('tickets')
      .select('shop_id, customer_id')
      .eq('id', params.ticketId)
      .single();

    if (ticketLookupErr || !ticketRow) {
      const msg = `[logTicketEventToOcis] Ticket ${params.ticketId} not found or RLS-blocked`;
      console.error(msg, ticketLookupErr);
      return { success: false, error: 'Ticket not found' };
    }

    if (ticketRow.shop_id !== params.shopId) {
      const msg = `[logTicketEventToOcis] shop mismatch — ticket.shop_id=${ticketRow.shop_id} caller.shopId=${params.shopId}`;
      console.error(msg);
      return { success: false, error: 'Ticket does not belong to the supplied shop' };
    }

    // OCIS-R68-FIX (HIGH-1): get-or-create the ticket_threads row before
    // inserting the event. Previously this helper read the existing thread
    // and inserted with `thread_id: thread?.id || null` — which for EVERY
    // first event per ticket (e.g. ticket_created, which is literally the
    // first OCIS call for every new ticket) left `thread_id = null` while
    // `ticket_id` was set, violating the `tte_ticket_thread_integrity`
    // CHECK constraint (ticket_id NOT NULL => thread_id NOT NULL). The
    // insert would then error, this code fell through to the edge-function
    // fallback (which DOES create the thread), and the ticket ended up
    // with a thread — but every first-event-per-ticket now costs an extra
    // network round-trip PLUS drops a misleading `[logTicketEventToOcis]
    // Direct insert failed` into the error log that drowns out real
    // failures. Creating the thread up front mirrors the server-side
    // `insertOcisThreadEvent` helper and closes this performance +
    // log-noise gap.
    let threadId: string | null = null;
    const { data: existingThread } = await supabase
      .from('ticket_threads')
      .select('id')
      .eq('ticket_id', params.ticketId)
      .maybeSingle();

    if (existingThread?.id) {
      threadId = existingThread.id;
    } else {
      // Idempotent upsert; lost races are recovered by re-querying.
      // customer_id is reused from the shop-mismatch validation lookup above.
      const { data: created } = await supabase
        .from('ticket_threads')
        .upsert(
          {
            ticket_id: params.ticketId,
            shop_id: params.shopId,
            customer_id: (ticketRow as { customer_id?: string | null }).customer_id ?? null,
          },
          { onConflict: 'ticket_id', ignoreDuplicates: true },
        )
        .select('id')
        .maybeSingle();
      if (created?.id) {
        threadId = created.id;
      } else {
        const { data: retry } = await supabase
          .from('ticket_threads')
          .select('id')
          .eq('ticket_id', params.ticketId)
          .maybeSingle();
        threadId = retry?.id ?? null;
      }
    }

    // Core-100 R2 (CORE-AF): an event written from the browser is a HUMAN act by the signed-in
    // user. It used to be recorded as source 'system' with no author whenever the caller omitted
    // authorUserId — so a person's action read as if the trusted system had generated it. The
    // database now refuses any other source from an end-user session and fills the author from
    // auth.uid(); this mirrors that so the row says exactly who did it.
    const authorUserId = params.authorUserId ?? (await supabase.auth.getUser()).data.user?.id ?? null;

    // Direct insert into ticket_thread_events (thread_id now guaranteed
    // when we have a ticketId, satisfying tte_ticket_thread_integrity).
    const insertData = {
      thread_id: threadId,
      ticket_id: params.ticketId,
      shop_id: params.shopId,
      event_type: params.eventType,
      event_source: 'user' as const,
      channel: 'system' as const,
      visibility: 'internal' as const,
      content: params.content,
      author_user_id: authorUserId,
      author_role: params.authorRole || null,
      payload: params.payload || {},
    };
    const { data: event, error } = await supabase
      .from('ticket_thread_events')
      .insert(insertData as any)
      .select('id')
      .single();

    if (error) {
      console.error('[logTicketEventToOcis] Direct insert failed, falling back to edge function:', error.message);
      // Fallback to edge function
      return ocisIngest({
        ticket_id: params.ticketId,
        shop_id: params.shopId,
        event_type: params.eventType,
        content: params.content,
        event_source: 'user',
        channel: 'system',
        visibility: 'internal',
        author_user_id: authorUserId ?? undefined,
        author_role: params.authorRole,
        payload: params.payload,
      });
    }

    return { success: true, event_id: event?.id, thread_id: threadId || undefined };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Unknown error';
    console.error('[logTicketEventToOcis] Error:', errorMessage);
    return { success: false, error: errorMessage };
  }
}

/**
 * Helper to log SMS events to OCIS
 */
export async function logSmsToOcis(params: {
  ticketId?: string;
  inboxThreadId?: string;
  shopId: string;
  direction: 'inbound' | 'outbound';
  content: string;
  customerId?: string;
  payload?: Record<string, unknown>;
}): Promise<OcisIngestResponse> {
  return ocisIngest({
    ticket_id: params.ticketId,
    inbox_thread_id: params.inboxThreadId,
    shop_id: params.shopId,
    event_type: params.direction === 'inbound' ? 'sms_inbound' : 'sms_outbound',
    content: params.content,
    event_source: params.direction === 'inbound' ? 'customer' : 'system',
    channel: 'sms',
    visibility: 'customer',
    customer_id: params.customerId,
    payload: params.payload,
  });
}

/**
 * Helper to log notes (technician, advisor, manager)
 */
export async function logNoteToOcis(params: {
  ticketId: string;
  shopId: string;
  noteType: 'technician' | 'advisor' | 'manager';
  content: string;
  authorUserId: string;
  isPinned?: boolean;
  payload?: Record<string, unknown>;
}): Promise<OcisIngestResponse> {
  const eventType: OcisEventType = `${params.noteType}_note`;
  
  return ocisIngest({
    ticket_id: params.ticketId,
    shop_id: params.shopId,
    event_type: eventType,
    content: params.content,
    event_source: 'user',
    channel: 'app',
    visibility: 'internal',
    author_user_id: params.authorUserId,
    author_role: params.noteType,
    is_pinned: params.isPinned,
    payload: params.payload,
  });
}

/**
 * Helper to log artifact events to OCIS.
 * Works with or without a ticket context — if ticketId is provided,
 * uses logTicketEventToOcis for thread attachment; otherwise uses
 * ocisIngest with shop-level scope.
 */
export async function logArtifactEventToOcis(params: {
  shopId: string;
  ticketId?: string;
  eventType: 'artifact_created' | 'artifact_linked';
  artifactId: string;
  storageKind: string;
  artifactType: string;
  linkedEntityType?: string;
  linkedEntityId?: string;
  role?: string;
  authorUserId?: string;
}): Promise<OcisIngestResponse> {
  const payload: Record<string, unknown> = {
    artifact_id: params.artifactId,
    storage_kind: params.storageKind,
    type: params.artifactType,
  };
  if (params.linkedEntityType) payload.linked_entity_type = params.linkedEntityType;
  if (params.linkedEntityId) payload.linked_entity_id = params.linkedEntityId;
  if (params.role) payload.role = params.role;

  const content =
    params.eventType === 'artifact_created'
      ? `Artifact uploaded (${params.artifactType})`
      : `Artifact linked as ${params.role || 'reference'}`;

  // If ticket context exists, route through the ticket thread system
  if (params.ticketId) {
    return logTicketEventToOcis({
      ticketId: params.ticketId,
      shopId: params.shopId,
      eventType: params.eventType,
      content,
      authorUserId: params.authorUserId,
      payload,
    });
  }

  // No ticket — shop-level ingest
  return ocisIngest({
    shop_id: params.shopId,
    event_type: params.eventType,
    content,
    // Core-100 R2 (CORE-AF): a browser call is a user act; ocis-ingest attributes it to the caller.
    event_source: 'user',
    channel: 'app',
    visibility: 'internal',
    author_user_id: params.authorUserId,
    payload,
  });
}
